import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Send, Paperclip, Download, Lock } from 'lucide-react';
import { formatDate } from '../client-portal/clientPortalApi';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

export default function AdminMessagesPanel({ clientId, token }) {
  const auth = { Authorization: `Bearer ${token}` };
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [internal, setInternal] = useState(false);
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);
  const endRef = useRef(null);

  const reload = async () => {
    const r = await fetch(`${backendUrl}/api/admin/clients/${clientId}/messages`, { headers: auth });
    if (r.ok) setMessages(await r.json());
    fetch(`${backendUrl}/api/admin/clients/${clientId}/messages/read`, { method: 'PATCH', headers: auth }).catch(() => {});
  };

  useEffect(() => {
    let stop = false;
    (async () => { try { await reload(); } finally { if (!stop) setLoading(false); } })();
    const t = setInterval(() => { if (!stop) reload().catch(() => {}); }, 15000);
    return () => { stop = true; clearInterval(t); };
    // eslint-disable-next-line
  }, [clientId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages.length]);

  const send = async () => {
    if (!draft.trim() || sending) return;
    setSending(true);
    try {
      await fetch(`${backendUrl}/api/admin/clients/${clientId}/messages`, {
        method: 'POST', headers: { ...auth, 'Content-Type': 'application/json' },
        body: JSON.stringify({ body: draft, internal_note: internal }),
      });
      setDraft('');
      await reload();
    } finally {
      setSending(false);
    }
  };

  const sendFile = async (file) => {
    if (!file) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (draft.trim()) fd.append('body', draft);
      fd.append('internal_note', internal ? 'true' : 'false');
      await fetch(`${backendUrl}/api/admin/clients/${clientId}/messages/upload`, {
        method: 'POST', headers: auth, body: fd,
      });
      setDraft('');
      await reload();
    } finally {
      setSending(false);
    }
  };

  const downloadAttachment = async (m) => {
    const res = await fetch(`${backendUrl}/api/admin/messages/${m.id}/attachment`, { headers: auth });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = m.attachment_filename || 'attachment';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  return (
    <div className="flex flex-col h-[60vh]">
      <div className="flex-1 overflow-y-auto p-4 space-y-2.5 bg-slate-50/40" data-testid="admin-messages-feed">
        {messages.length === 0 && (
          <div className="text-center text-slate-400 py-12 text-sm">No messages yet.</div>
        )}
        {messages.map((m) => {
          const isClient = m.sender_type === 'client';
          const isInternal = m.internal_note;
          return (
            <div key={m.id} className={`flex ${isClient ? 'justify-start' : 'justify-end'}`}>
              <div className={`max-w-[78%] rounded-2xl px-3 py-2 text-sm ring-1 ${
                isInternal
                  ? 'bg-amber-50 ring-amber-200 text-slate-800'
                  : isClient
                    ? 'bg-white ring-slate-200 text-slate-800'
                    : 'bg-emerald-500 ring-emerald-500 text-white'
              }`}>
                <div className="text-[10px] font-medium opacity-80 mb-0.5 flex items-center gap-1">
                  {isInternal && <Lock className="w-3 h-3" />}
                  {isInternal ? 'Internal note' : m.sender_name}
                </div>
                {m.body && <div className="whitespace-pre-wrap">{m.body}</div>}
                {m.has_attachment && (
                  <button
                    onClick={() => downloadAttachment(m)}
                    className={`mt-1.5 inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded ring-1 ${
                      isInternal ? 'bg-white ring-amber-200' :
                      isClient ? 'bg-slate-50 ring-slate-200' : 'bg-emerald-600/40 ring-emerald-300/40'
                    }`}
                  >
                    <Download className="w-3 h-3" /> {m.attachment_filename}
                  </button>
                )}
                <div className="text-[10px] opacity-70 mt-0.5">{formatDate(m.created_at)}</div>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      <div className="border-t border-slate-100 p-3 bg-white">
        <label className="flex items-center gap-1.5 text-xs text-slate-600 mb-2">
          <input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} data-testid="internal-note-toggle" />
          Internal note (only the team can see)
        </label>
        <div className="flex items-end gap-2">
          <input
            type="file" ref={fileRef} className="hidden"
            onChange={(e) => sendFile(e.target.files?.[0])}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="p-2 rounded-lg ring-1 ring-slate-200 text-slate-600 hover:bg-slate-50"
            disabled={sending}
            aria-label="Attach"
          >
            <Paperclip className="w-4 h-4" />
          </button>
          <textarea
            rows={1}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={internal ? 'Type internal note…' : 'Reply to client…'}
            data-testid="admin-message-input"
            className="flex-1 px-3 py-2 rounded-lg ring-1 ring-slate-200 resize-none focus:outline-none focus:ring-2 focus:ring-emerald-400 text-sm"
          />
          <button
            onClick={send}
            disabled={sending || !draft.trim()}
            data-testid="admin-message-send"
            className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white disabled:opacity-50"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}
